<template>
  <div class="users-page">
    <APageHead title="저자 계정 관리" sub="콘텐츠 저자 계정을 효율적으로 관리합니다.">
      <template #actions>
        <ABtn kind="primary" icon="person_add" @click="addNewEditor">새 저자 추가</ABtn>
      </template>
    </APageHead>

    <div class="users-page__stats">
      <ACard><AStat label="전체 저자" :value="formatNum(totalCount)" unit="명" /></ACard>
      <ACard><AStat label="활성 계정" :value="formatNum(activeCount)" unit="명" /></ACard>
      <ACard><AStat label="비활성 계정" :value="formatNum(inactiveCount)" unit="명" /></ACard>
    </div>

    <ACard overflow="visible">
      <SearchArea :search-fields="searchFields" @search="handleSearch" />
    </ACard>

    <ACard>
      <template #title>
        <span class="users-page__title">{{ $t('navi.03_02') }}</span>
      </template>
      <template #sub>저자 계정 목록 및 상태 관리</template>
      <template #action>
        <APill kind="info">총 {{ formatNum(totalCount) }}명</APill>
      </template>

      <AEmpty v-if="tableData.length === 0" title="등록된 저자가 없어요" />

      <ATable v-else :columns="columns" :rows="tableData" row-key="idx">
        <template #cell:no="{ index }"><span class="users-page__num">{{ getItemNumber(index) }}</span></template>
        <template #cell:name="{ row }"><span class="users-page__name">{{ row.name }}</span></template>
        <template #cell:email="{ row }"><span class="users-page__mono">{{ row.email }}</span></template>
        <template #cell:phone="{ row }">{{ row.phone }}</template>
        <template #cell:memo="{ row }"><small class="users-page__muted">{{ row.memo }}</small></template>
        <template #cell:currency="{ row }">{{ row.preferred_currency }}</template>
        <template #cell:lang="{ row }">{{ row.first_language }}</template>
        <template #cell:status="{ row }">
          <APill v-if="row.active_yn === 'Y'" kind="ok" dot>{{ $t('label.65') }}</APill>
          <APill v-else kind="warn" dot>{{ $t('label.66') }}</APill>
        </template>
        <template #cell:actions="{ row }">
          <ABtn size="sm" icon="visibility" @click="viewDetail(row)">{{ $t('btn.24') }}</ABtn>
        </template>
      </ATable>

      <APagination
        v-if="totalCount > 0"
        :current-page="currentPage"
        :total="totalCount"
        :page-size="itemsPerPage"
        @change="(p) => { currentPage = p; getData(); }"
      />
    </ACard>

    <EditorCreateModal :visible="showAddModal" @close="showAddModal = false" @success="getData" />
  </div>
</template>

<script>
import SearchArea from "@/components/SearchArea.vue";
import EditorCreateModal from "@/components/Users/EditorCreateModal.vue";
import APageHead from "@/components/Pineapple/APageHead.vue";
import ABtn from "@/components/Pineapple/ABtn.vue";
import ACard from "@/components/Pineapple/ACard.vue";
import AStat from "@/components/Pineapple/AStat.vue";
import APill from "@/components/Pineapple/APill.vue";
import ATable from "@/components/Pineapple/ATable.vue";
import AEmpty from "@/components/Pineapple/AEmpty.vue";
import APagination from "@/components/Pineapple/APagination.vue";

export default {
  name: "UsersEditors",
  components: { SearchArea, EditorCreateModal, APageHead, ABtn, ACard, AStat, APill, ATable, AEmpty, APagination },
  data() {
    return {
      tableData: [],
      currentPage: 1,
      itemsPerPage: 10,
      totalCount: 0,
      searchValues: {},
      showAddModal: false,
      searchFields: [
        { key: "name", placeholder: this.$t("label.29"), col: "col-md-2" },
        { key: "email", placeholder: this.$t("label.01"), col: "col-md-2" },
        {
          key: "active_yn", placeholder: this.$t("label.51"), col: "col-md-2", type: "select",
          options: [
            { value: "Y", label: this.$t("label.65") },
            { value: "N", label: this.$t("label.66") },
          ],
        },
      ],
      columns: [
        { key: "no", label: "번호", align: "center", width: 60 },
        { key: "name", label: this.$t("label.29"), width: 140 },
        { key: "email", label: this.$t("label.01") },
        { key: "phone", label: this.$t("label.10"), width: 130 },
        { key: "memo", label: this.$t("label.67"), wrap: true, width: 200 },
        { key: "currency", label: this.$t("label.32"), width: 90 },
        { key: "lang", label: this.$t("label.03"), width: 90 },
        { key: "status", label: this.$t("label.51"), width: 110 },
        { key: "actions", label: "액션", align: "right", width: 110 },
      ],
    };
  },
  computed: {
    userInfo() { return this.$store.state.auth.user_info || {}; },
    getItemNumber() {
      return (index) => this.totalCount - ((this.currentPage - 1) * this.itemsPerPage + index);
    },
    activeCount() { return this.tableData.filter((i) => i.active_yn === "Y").length; },
    inactiveCount() { return this.tableData.filter((i) => i.active_yn === "N").length; },
  },
  mounted() { if (this.userInfo && this.userInfo.name) this.getData(); },
  methods: {
    formatNum(n) { return Number(n || 0).toLocaleString(); },
    async getData() {
      try {
        const res = await this.$axios({
          method: "get",
          url: "/roles/editors",
          params: {
            limit: this.itemsPerPage,
            page: this.currentPage,
            search_params: JSON.stringify(this.searchValues),
          },
          loadingBar: true,
        });
        if (res.status === 200) {
          this.tableData = res.data.items;
          this.totalCount = res.data.total_count;
        }
      } catch (error) {
        console.error("Error :", error);
      }
    },
    handleSearch(searchValues) {
      this.searchValues = searchValues;
      this.currentPage = 1;
      this.getData();
    },
    viewDetail(item) {
      this.$router.push({ name: "EditorDetail", query: { idx: item.idx } });
    },
    addNewEditor() { this.showAddModal = true; },
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/scss/pineapple";

.users-page {
  display: flex;
  flex-direction: column;
  gap: 20px;

  &__stats {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 16px;
    @media (max-width: 992px) { grid-template-columns: repeat(2, 1fr); }
    @media (max-width: 640px) { grid-template-columns: 1fr; }
  }

  &__title { font-size: 13px; font-weight: 800; color: $p-ink; }
  &__num { font-family: $p-font-mono; color: $p-ink-mid; font-weight: 700; }
  &__mono { font-family: $p-font-mono; color: $p-ink2; font-size: 12px; }
  &__name { font-weight: 700; color: $p-ink; }
  &__muted { color: $p-ink-soft; }
}
</style>
